import { useLayoutEffect, useState } from 'react';

/**
 * SCAN-004 — where the picture actually is inside the box holding it.
 *
 * Both scanning screens draw on top of an image: the viewfinder puts an outline
 * on the camera's frame, the editor puts four handles on a photograph. Both are
 * therefore only as accurate as their answer to "which pixels of this element
 * are the picture?", and `object-contain` leaves a margin down one pair of
 * sides whose size depends on the picture's proportions and the box's — neither
 * of which is known until both have arrived.
 *
 * Getting it wrong is not subtle: the outline sits a centimetre off the paper,
 * and a dragged corner lands somewhere other than under the finger.
 */
export interface ContainRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Takes the element rather than a ref to it, because a ref's contents changing
 * is not something a hook can be told about: the editor swaps its picture for a
 * different element when the view changes, and a hook watching `ref.current`
 * from an effect that ran on mount goes on measuring the element that left.
 */
export function useContainRect(
  element: HTMLElement | null,
  natural: { width: number; height: number } | null,
): ContainRect | null {
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (!element) {
      setBox(null);
      return;
    }

    // Measured rather than assumed, and re-measured rather than measured once:
    // the box changes size when the phone is turned, when the keyboard opens,
    // and when the page strip below it gains its first thumbnail.
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setBox((current) =>
        current && current.width === width && current.height === height ? current : { width, height },
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  if (!box || !natural || natural.width <= 0 || natural.height <= 0) return null;
  if (box.width <= 0 || box.height <= 0) return null;

  const scale = Math.min(box.width / natural.width, box.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;
  return { left: (box.width - width) / 2, top: (box.height - height) / 2, width, height };
}
